import React, { useState, useEffect, useCallback } from 'react';
import { Container, Button, Card } from 'react-bootstrap';
import styles from '../styles/Events.module.css';
import eventApi from '../../api/events/eventApi';
import { Link } from 'react-router-dom';
import { FaSpinner, FaCalendarAlt } from 'react-icons/fa';

const PLACEHOLDER = 'https://via.placeholder.com/480x320?text=No+Image';

const Events = () => {
  const [filter, setFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchEvents = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);

      const result = await eventApi.getPublicEvents(page);

      setEvents(prev => append ? [...prev, ...result.events] : result.events);
      setCurrentPage(result.currentPage);
      setTotalPages(result.totalPages);
      setTotalRecords(result.totalRecords);
    } catch (err) {
      console.error('Failed to load events', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents(1, false);
  }, [fetchEvents]);

  const handleLoadMore = () => {
    if (currentPage < totalPages && !loadingMore) {
      fetchEvents(currentPage + 1, true);
    }
  };

  const generateWebsiteUrl = (event) => {
    if (!event || !event.event_title) return '#';
    const slug = event.event_title.toLowerCase().replace(/\s+/g, '');
    const stateData = {
      event, template: 1, theme: 'light', color: 'blue',
      primaryColor: '#4a85ff', secondaryColor: '#0666e0',
    };
    return `/event-website/${slug}?state=${encodeURIComponent(JSON.stringify(stateData))}`;
  };

  const filteredEvents = filter === 'all'
    ? events
    : events.filter(e => e.event_type?.toLowerCase() === filter);

  const getImage = (wallpaper) => {
    if (!wallpaper) return PLACEHOLDER;
    // wallpaper is an object: { lg_photo, md_photo, sm_photo }
    return wallpaper.md_photo || wallpaper.lg_photo || wallpaper.sm_photo || PLACEHOLDER;
  };

  return (
    <section className={styles.eventsSection}>
      <Container>
       
        <h2 className="text-center mb-4 stylish-heading">All Events</h2>

        {/* Total count badge */}
        {totalRecords > 0 && (
          <p className="text-center text-muted mb-3" style={{ fontSize: '0.9rem' }}>
            Showing <strong>{events.length}</strong> of <strong>{totalRecords}</strong> events
          </p>
        )}

        {/* Filter buttons */}
        <div className={styles.filterButtons + ' text-center mb-4'}>
          {['all', 'harusi', 'kitchen party', 'send-off', 'anniversary', 'Engagement'].map(type => (
            <Button
              key={type}
              variant={filter === type ? 'primary' : 'outline-primary'}
              onClick={() => setFilter(type)}
              style={{
                borderRadius: '20px', margin: '5px',
                backgroundColor: filter === type ? '#007bff' : 'transparent',
                color: filter === type ? 'white' : '#007bff',
              }}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </Button>
          ))}
        </div>

        {/* Events grid */}
        {loading ? (
          <div className="text-center p-5">
            <FaSpinner className="fa-spin" size={40} color="#4169e1" />
            <p className="mt-3" style={{ color: '#4169e1', fontWeight: 600 }}>Loading events...</p>
          </div>
        ) : (
          <>
            <div className={styles.eventsGrid}>
              {filteredEvents.length === 0 ? (
                <p className="text-center text-muted w-100">No events found for this filter.</p>
              ) : (
                filteredEvents.map(event => (
                  <Card key={event.id} className={styles.eventCard}>
                    <Link to={generateWebsiteUrl(event)} target="_blank" rel="noopener noreferrer">
                      <Card.Img
                        variant="top"
                        src={getImage(event.wallpaper)}
                        alt={event.event_title}
                        onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER; }}
                      />
                    </Link>
                    <Card.Body className="d-flex flex-column pb-1">
                      <Card.Title
                        className="text-truncate fw-bold mb-1"
                        title={event.event_title}
                        style={{ color: '#24366b', fontSize: '1.1rem' }}
                      >
                        {event.event_title}
                      </Card.Title>
                      <Card.Text className="text-muted small mb-2">{event.event_type}</Card.Text>
                    </Card.Body>
                    <Card.Footer className="bg-white border-top-0 pt-0 pb-3">
                      <div className="d-flex align-items-center" style={{ color: '#666' }}>
                        <FaCalendarAlt className="me-2" style={{ color: '#4a85ff' }} />
                        <small style={{ fontWeight: 600, letterSpacing: '0.5px', fontSize: '0.85rem' }}>
                          {(() => {
                            const d = new Date(event.event_date);
                            if (isNaN(d)) return '';
                            return `${d.toLocaleString('en-US', { month: 'long' })}- ${d.getDate()} - ${d.getFullYear()}`;
                          })()}
                        </small>
                      </div>
                    </Card.Footer>
                  </Card>
                ))
              )}
            </div>

            {/* Load More button */}
            {currentPage < totalPages && (
              <div className="text-center mt-4 mb-4">
                <Button
                  variant="outline-primary"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  style={{
                    borderRadius: '30px', padding: '10px 36px',
                    fontWeight: '700', fontSize: '0.95rem',
                    display: 'inline-flex', alignItems: 'center', gap: '8px'
                  }}
                >
                  {loadingMore ? (
                    <><FaSpinner className="fa-spin" /> Loading...</>
                  ) : (
                    <>Load More Events</>
                  )}
                </Button>
                <p className="text-muted mt-2" style={{ fontSize: '0.8rem' }}>
                  Page {currentPage} of {totalPages}
                </p>
              </div>
            )}
          </>
        )}
      </Container>
    </section>
  );
};

export default Events;